import {autoserializeAs, autoserializeUsing, inheritSerialization} from "dcerialize";
import type {DateTime} from "luxon";
import {DateOnlySerializer} from "../../helpers/DateSerializers";
import {
  createExisting,
  createNew,
  type Existing,
  type IdentifiableModel,
  type IdOf,
  type New,
} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import {ModificationDetails} from "../ModificationDetails";
import {IdOnlyCurrency} from "./Currency";

export class IdOnlyExchangeRate implements IdentifiableModel {
  @autoserializeAs(() => Number)
  public id?: number;

  public static existing(id: IdOf<IdOnlyExchangeRate>): Existing<IdOnlyExchangeRate> {
    return createExisting(IdOnlyExchangeRate, id);
  }
}

@inheritSerialization(() => IdOnlyExchangeRate)
export class RateOnlyExchangeRate extends IdOnlyExchangeRate {
  @autoserializeAs(() => Number)
  public rate: number;
}

@inheritSerialization(() => RateOnlyExchangeRate)
export class WritableExchangeRate extends RateOnlyExchangeRate {
  @autoserializeAs(() => IdOnlyCurrency)
  public readonly sourceCurrency: Readonly<Existing<IdOnlyCurrency>>;
  @autoserializeAs(() => IdOnlyCurrency)
  public targetCurrency: Existing<IdOnlyCurrency>;
  @autoserializeUsing(DateOnlySerializer)
  public startDate: DateTime<true>;
  @autoserializeUsing(DateOnlySerializer)
  public endDate: DateTime<true>;

  public get isIdentity(): boolean {
    return this.sourceCurrency.id === this.targetCurrency.id;
  }

  public static createNew(sourceCurrency: Readonly<Existing<IdOnlyCurrency>>,
                          targetCurrency: Readonly<Existing<IdOnlyCurrency>>,
                          rate: number,
                          startDate: DateTime<true>,
                          endDate: DateTime<true>): New<WritableExchangeRate> {
    const exchangeRate = createNew(WritableExchangeRate);
    exchangeRate.targetCurrency = targetCurrency;
    exchangeRate.rate = rate;
    exchangeRate.startDate = startDate;
    exchangeRate.endDate = endDate;
    return Object.assign(exchangeRate, {sourceCurrency: sourceCurrency});
  }
}

@inheritSerialization(() => WritableExchangeRate)
export class SimpleExchangeRate extends WritableExchangeRate {
}

/**
 * @sealed
 */
@inheritSerialization(() => SimpleExchangeRate)
export class ExchangeRate extends SimpleExchangeRate implements ModificationTrackedModel {
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}
